import React, { useEffect, useState } from 'react';
import { useQuery } from 'hooks';
import { useDebouncedEffect } from 'hooks/useDebouncedEffect/useDebouncedEffect';
import { AdminCourseService } from 'services/admin-course.service';
import { ResponsePagination } from 'models/response-pagination';
import { CourseModel } from 'models/simple-certificates/course.model';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Button,
    Column,
    Container,
    EmptyListWarning,
    GridFixed,
    Icon,
    Loader,
    Row,
    TextInput,
    Title,
} from 'components';
import moment from 'moment';
import { dateTimeFormat } from 'constants/date-format';
import { DEFAULT_PAGE_SIZE } from 'constants/default-page-size';

const sizeUrlParam = 'size';
const pageUrlParam = 'page';
const queryUrlParam = 'query';

const CoursesListPage = () => {
    const queryParams = useQuery();

    const { push, replace } = useHistory();
    const { pathname } = useLocation();

    const urlSize = +(queryParams.get(sizeUrlParam) as string);
    const urlPage = +(queryParams.get(pageUrlParam) as string);
    const urlQuery = queryParams.get(queryUrlParam) || '';
    const [courseName, setCourseName] = useState('');
    const [loading, setLoading] = useState(false);
    const [courses, setCourses] = useState<ResponsePagination<CourseModel>>({
        count: 0,
        items: [],
    });

    const getCourses = () => {
        if (urlSize === 0) return;
        setLoading(true);
        AdminCourseService.getCourses({
            page: urlPage,
            size: urlSize,
            name: urlQuery,
        })
            .then((d) => {
                setCourses(d.data);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useDebouncedEffect(
        () => {
            queryParams.set(pageUrlParam, `0`);
            queryParams.set(sizeUrlParam, `${DEFAULT_PAGE_SIZE}`);
            queryParams.set(queryUrlParam, courseName);
            replace({
                search: queryParams.toString(),
                pathname,
            });
        },
        [courseName],
        400
    );

    useEffect(() => {
        getCourses();
    }, [urlSize, urlPage, urlQuery]);

    const getExcel = () => {
        AdminCourseService.getCoursesXls({
            name: urlQuery,
        });
    };

    const deleteHandle = (id: string) => {
        if (window.confirm('Вы уверены?')) {
            setLoading(true);
            AdminCourseService.delete(id)
                .then(() => getCourses())
                .finally(() => setLoading(true));
        }
    };

    const setPageSize = (page: number, size: number) => {
        queryParams.set(pageUrlParam, `${page}`);
        queryParams.set(sizeUrlParam, `${size}`);
        replace({
            search: queryParams.toString(),
            pathname,
        });
    };

    const getGridData = (): Row[] =>
        courses.items.map((item) => ({
            ...item,
            creationDate: moment(item.creationDate).format(dateTimeFormat),
            updateActions: {
                additionalComponent: (
                    <Button pSize="none" handleClick={() => push(`${pathname}/edit/${item.id}`)}>
                        <Icon iconName="edit" />
                    </Button>
                ),
            },
            deleteActions: {
                additionalComponent: (
                    <Button pSize="none" handleClick={() => deleteHandle(item.id)}>
                        <Icon iconName="delete" />
                    </Button>
                ),
            },
        }));

    return (
        <>
            {loading && <Loader isBlock />}
            <Container>
                <Column columnSize="50">
                    <Title titleText="Курсы" />
                </Column>
                <Column columnSize="100">
                    <TextInput
                        id="search-field"
                        label="Название"
                        value={courseName}
                        onChangeHandler={(e) => setCourseName(e.target.value)}
                    />
                </Column>
                <Column columnSize="100" className="text-right">
                    <Button buttonClass="primary" handleClick={() => push(`${pathname}/add`)}>
                        Добавить
                    </Button>
                    {courses && courses.count !== 0 && (
                        <Button buttonClass="primary" handleClick={getExcel}>
                            Excel
                        </Button>
                    )}
                </Column>
                {courses && courses.count !== 0 ? (
                    <Column columnSize="100" className="top-space-10x">
                        <GridFixed
                            columns={getColumns()}
                            rows={getGridData()}
                            pagination={{
                                total: courses.count,
                                page: urlPage,
                                size: urlSize,
                                handleChange: setPageSize,
                            }}
                        />
                    </Column>
                ) : (
                    <EmptyListWarning />
                )}
            </Container>
        </>
    );
};

export default CoursesListPage;

const getColumns = () => ({
    code: {
        value: 'Код курса',
        columnSize: '200',
    },
    name: {
        value: 'Название курса',
        columnSize: '400',
    },
    updateActions: {
        value: '',
        columnSize: '10',
    },
    deleteActions: {
        value: '',
        columnSize: '10',
    },
});
